<template>
  <PremiumModalForm
    v-model="dialog"
    title="Add Unit"
    submit-text="Save Unit"
    :loading="saving"
    :max-width="480"
    @submit="submit"
  >
    <v-form ref="addUnitForm" v-model="validForm" lazy-validation>

      <!-- Unit Type Toggle (mixed or commercial only) -->
      <div v-if="showUnitTypeToggle" class="mb-5">
        <span class="pmf-lbl d-block mb-2">Unit Type</span>
        <div class="unit-type-toggle">
          <button
            v-if="!isCommercialOnly"
            type="button"
            class="unit-type-btn"
            :class="{ active: form.unit_type === '1' }"
            @click="form.unit_type = '1'"
          >
            <v-icon small class="mr-1">mdi-home-outline</v-icon>
            House
          </button>
          <button
            type="button"
            class="unit-type-btn"
            :class="{ active: form.unit_type === '2' }"
            @click="form.unit_type = '2'"
          >
            <v-icon small class="mr-1">mdi-store-outline</v-icon>
            Shop
          </button>
        </div>
      </div>

      <!-- Block / Wing -->
      <div v-if="hasMultipleBlocks && !(form.unit_type === '2' && isMixed)" class="mb-4">
        <div class="d-flex justify-space-between align-end mb-1">
          <span class="pmf-lbl">Block  <span v-if="form.unit_type !== '2'" class="pmf-req">*</span></span>
          <a v-if="!addingBlock" href="javascript:void(0)" class="pmf-link" @click="startAddBlock">
            + Add Block
          </a>
        </div>
        <template v-if="!addingBlock">
          <v-select
            v-model="form.flat_id"
            :items="blocks"
            item-text="name"
            item-value="id"
            placeholder="Select Block"
            outlined
            dense
            hide-details="auto"
            class="pmf-input"
            clearable
            prepend-inner-icon="mdi-office-building-outline"
            :rules="form.unit_type !== '2' ? [v => !!v || 'Block is required'] : []"
          ></v-select>
        </template>
        <template v-else>
          <div class="d-flex align-start" style="gap: 8px;">
            <div style="flex:1; display:flex; flex-direction:column;">
              <v-text-field
                v-model="newBlockName"
                placeholder="Enter block name"
                outlined
                dense
                :error-messages="newBlockNameError"
                hide-details="auto"
                class="pmf-input ma-0 pa-0"
                autofocus
                @input="newBlockNameError = ''"
                @keyup.enter="addBlock"
              ></v-text-field>
            </div>
            <div style="display:flex; gap:8px; padding-top:1px; flex-shrink:0;">
              <button type="button" class="pmf-save-btn" :disabled="savingBlock" @click="addBlock">
                <v-progress-circular v-if="savingBlock" indeterminate size="13" width="2" color="#fff" class="mr-1" />
                Save
              </button>
              <button type="button" class="pmf-cancel-btn" @click="cancelAddBlock">
                Cancel
              </button>
            </div>
          </div>
        </template>
      </div>

      <!-- Unit Number -->
      <div class="mb-2">
        <span class="pmf-lbl d-block mb-1">
          {{ unitNumberLabel }} <span class="pmf-req">*</span>
        </span>
        <v-text-field
          v-model="form.house_number"
          :placeholder="unitNumberPlaceholder"
          outlined
          dense
          hide-details="auto"
          :rules="[v => !!v || `${unitNumberLabel} is required`]"
          class="pmf-input"
          :prepend-inner-icon="form.unit_type === '2' ? 'mdi-store-outline' : 'mdi-home-outline'"
        ></v-text-field>
      </div>

    </v-form>
  </PremiumModalForm>
</template>

<script>
import { mapMutations } from "vuex";
import PremiumModalForm from "../../common/PremiumModalForm.vue";

export default {
  name: "AddUnitModal",
  components: { PremiumModalForm },
  props: {
    society_id: {
      type: String,
      required: true,
    },
    society: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dialog: false,
      validForm: true,
      saving: false,
      savingBlock: false,
      addingBlock: false,
      newBlockName: "",
      newBlockNameError: "",
      blocks: [],
      form: {
        unit_type: "1",
        flat_id: null,
        house_number: "",
        occupancy_status: "vacant",
      },
    };
  },
  computed: {
    societyType() {
      return Number(this.society.type || 1);
    },
    hasMultipleBlocks() {
      return (
        this.society.has_multiple_building === 1 ||
        this.society.has_multiple_building === "1" ||
        this.society.has_multiple_building === true
      );
    },
    isCommercialOnly() {
      return this.societyType === 2;
    },
    isMixed() {
      return this.societyType === 3;
    },
    showUnitTypeToggle() {
      return this.isMixed || this.isCommercialOnly;
    },
    unitNumberLabel() {
      if (this.form.unit_type === "2") return "Shop Number";
      return this.hasMultipleBlocks ? "Unit Number" : "House Number";
    },
    unitNumberPlaceholder() {
      return this.form.unit_type === "2" ? "e.g. S1, S-02" : "e.g. 101, A-102";
    },
  },
  watch: {
    societyType: {
      immediate: true,
      handler(val) {
        if (val === 2) {
          this.form.unit_type = "2";
        } else {
          this.form.unit_type = "1";
        }
      },
    },
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),

    openModal() {
      this.form = {
        unit_type: this.isCommercialOnly ? "2" : "1",
        flat_id: null,
        house_number: "",
        occupancy_status: "vacant",
      };
      this.addingBlock = false;
      this.newBlockName = "";
      this.newBlockNameError = "";
      if (this.$refs.addUnitForm) {
        this.$refs.addUnitForm.resetValidation();
      }
      if (this.hasMultipleBlocks) {
        this.fetchBlocks();
      }
      this.dialog = true;
    },

    fetchBlocks(selectNewId = null) {
      this.$axios.get(`society/${this.society_id}/flats`).then((res) => {
        if (res.data.status) {
          this.blocks = res.data.flats || [];
          if (selectNewId) {
            this.form.flat_id = selectNewId;
          }
        }
      });
    },

    startAddBlock() {
      this.newBlockName = "";
      this.newBlockNameError = "";
      this.addingBlock = true;
    },

    cancelAddBlock() {
      this.addingBlock = false;
      this.newBlockName = "";
      this.newBlockNameError = "";
    },

    addBlock() {
      if (!this.newBlockName.trim()) {
        this.newBlockNameError = "Block name is required";
        return;
      }
      if (this.newBlockName.trim().length > 50) {
        this.newBlockNameError = "Block name must be 50 characters or less";
        return;
      }
      this.newBlockNameError = "";
      this.savingBlock = true;
      this.$axios.post(`superadmin/society/${this.society_id}/flats`, { name: this.newBlockName.trim() })
        .then(res => {
          this.savingBlock = false;
          if (res.data.status) {
            this.addingBlock = false;
            this.newBlockName = "";
            this.newBlockNameError = "";
            this.setAlert({ showAlert: true, variant: "green accent-4", timeout: 3000, message: "Block added successfully" });
            const newId = res.data.data ? res.data.data.id : null;
            this.fetchBlocks(newId);
          } else {
            const msg = (res.data.errors && res.data.errors.length > 0)
              ? res.data.errors[0].message
              : (res.data.message || "Failed to add block");
            this.newBlockNameError = msg;
          }
        })
        .catch(() => {
          this.savingBlock = false;
          this.setAlert({ showAlert: true, variant: "red accent-3", timeout: 3000, message: "Something went wrong" });
        });
    },

    submit() {
      if (!this.$refs.addUnitForm.validate()) return;
      this.saving = true;
      this.$axios
        .post(`superadmin/society/${this.society_id}/units`, this.form)
        .then((res) => {
          this.saving = false;
          if (res.data.status) {
            this.setAlert({ showAlert: true, variant: "green accent-4", timeout: 3000, message: res.data.message || "Unit added successfully" });
            this.dialog = false;
            this.$emit("unit-added");
          } else {
            this.setAlert({ showAlert: true, variant: "red accent-3", timeout: 3000, message: res.data.message || "Failed to add unit" });
          }
        })
        .catch(() => {
          this.saving = false;
          this.setAlert({ showAlert: true, variant: "red accent-3", timeout: 3000, message: "Something went wrong" });
        });
    },
  },
};
</script>

<style scoped>
.pmf-lbl {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  text-transform: uppercase;
}
.pmf-req { color: #ef4444; }

.pmf-link {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--accent);
  text-decoration: none;
  cursor: pointer;
}
.pmf-link:hover { text-decoration: underline; }

/* Unit Type Toggle */
.unit-type-toggle {
  display: flex;
  gap: 10px;
}
.unit-type-btn {
  flex: 1;
  padding: 10px 16px;
  border: 2px solid var(--border-color2);
  border-radius: 10px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}
.unit-type-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
  background: rgba(var(--accent-rgb, 234, 88, 12), 0.06);
}
.unit-type-btn.active {
  background: rgba(var(--accent-rgb, 234, 88, 12), 0.08);
  border-color: var(--accent);
  color: var(--accent);
  font-weight: 700;
}

.pmf-save-btn {
  height: 38px;
  padding: 0 14px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-shrink: 0;
}
.pmf-save-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.pmf-cancel-btn {
  height: 38px;
  padding: 0 12px;
  background: var(--border-color2, rgba(0,0,0,0.06));
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}
.pmf-cancel-btn:hover { background: rgba(0,0,0,0.1); }

.pmf-input :deep(.v-input__slot) {
  background: var(--bg-primary, #f8fafc) !important;
  border-radius: 8px !important;
}
</style>
